import {
  type BrandConfig,
  type LegalConfig,
  type LegalDocumentLinkSlot,
  brandName,
  legalLinkLabel,
} from '@kitup/brand';
import type { Localized } from './types.js';

/**
 * Russian numeral plural form. Lives in the locale dictionary, not in shared code:
 * the set of forms is a property of the language, and the next locale will declare its own.
 */
function plural(count: number, one: string, few: string, many: string): string {
  const rule = new Intl.PluralRules('ru-RU').select(count);
  if (rule === 'one') return one;
  if (rule === 'few') return few;
  return many;
}

const ruCount = (value: number): string => value.toLocaleString('ru-RU');

/**
 * What redrawing a picture costs: `null` — nothing (no limit on this book), `undefined` —
 * a replacement, count not known yet, 0 — none left, so a pack is needed first,
 * `'payment'` — an unpaid book, whose free picture is redrawn only after paying.
 */
function ruRedrawCost(remaining: number | null | undefined | 'payment'): string {
  if (remaining === 'payment') return 'а перерисовать её можно будет только после оплаты книги.';
  if (remaining === null) return 'но её можно будет нарисовать заново.';
  if (remaining === undefined) return 'а перерисовка спишет одну замену.';
  if (remaining === 0)
    return 'а бесплатные замены закончились: чтобы перерисовать её, понадобится пакет замен.';
  return `а перерисовка спишет одну бесплатную замену (осталось ${ruCount(remaining)}).`;
}

/**
 * The Russian locale is the reference structure of the dictionaries.
 *
 * The dictionary is built from the brand rather than importing it: the name and prices are
 * brand facts, not language facts, and are substituted here so the same text
 * works for any brand that speaks Russian. The legal bundle comes alongside,
 * for the document names that appear inside sentences.
 */
function ruSourceOf(brand: BrandConfig, legal: LegalConfig) {
  const name = brandName(brand, 'ru');
  const legalLink = (slot: LegalDocumentLinkSlot): string =>
    legalLinkLabel(brand, legal, slot, 'ru');
  return {
    app: {
      name,
      sections: 'Разделы',
      tagline: 'Персональная детская книга за десять минут',
    },
    meta: {
      title: `${name} — персональные детские книги`,
      description:
        'Соберите персональную детскую книгу с вашим ребёнком в главной роли за десять минут.',
      ogImageAlt: brand.ogImageAlt['ru'] ?? '',
    },
    /**
     * Demo book: the same templates and the same page flipper as the real one,
     * but the data lives in code and needs neither an account nor generations (E15-T2).
     */
    demo: {
      title: 'Мира и добрая звезда',
      subtitle: 'Демонстрационная книга',
      dedication: 'Тому, кто ещё не умеет читать, но уже умеет слушать.',
      bannerTitle: 'Это демонстрационная книга',
      bannerText:
        'Так выглядит готовая книга: листайте стрелками, свайпом или ползунком. Иллюстрации здесь нарисованы заглушками.',
      start: 'Собрать свою книгу',
      toBooks: 'К моим книгам',
      home: 'На главную',
      spreads: [
        'Мира нашла на чердаке старую карту.\nНа ней были холмы, река и крестик из трёх лучей.\n«Да это же звезда», — сказала Мира.',
        'Заяц Ушастик выходить из дома не хотел.\nНо Мира взяла его за лапу — и стало совсем не страшно.\nДорога сама легла под ноги.',
        'За холмами и вправду светила добрая звезда.\nОна показывала путь тем, кто идёт вдвоём.\nМира с Ушастиком шли и считали шаги.',
        'Дома их ждал тёплый свет в окне.\nМира спрятала карту под подушку.\n«Завтра пойдём снова», — прошептала она.',
      ],
    },
    /** A hint for newcomers instead of an empty book list (01-SPEC.md §4.1). */
    onboarding: {
      title: 'Первая книга за четыре шага',
      steps: [
        {
          title: 'Основное',
          text: 'Тариф книги, стиль сказки и вид разворотов. Тариф фиксируется, когда написан сюжет, остальное можно менять.',
        },
        { title: 'Герои', text: 'Анкета и три фотографии. Героя рисуем мы, утверждаете вы.' },
        {
          title: 'Сюжет',
          text: 'Пара фраз о том, что случится, — история разложится по разворотам.',
        },
        {
          title: 'Иллюстрации и экспорт',
          text: 'Первый разворот рисуем бесплатно. После оплаты — остальные развороты, обложки, ссылка для семьи и PDF.',
        },
      ],
      start: 'Начать первую книгу',
      demo: 'Сначала полистать демо-книгу',
    },
    placeholder: {
      underConstruction: 'Приложение собирается. Экраны появятся в следующих эпиках.',
    },
    common: {
      loading: 'Загружаем…',
      reconnecting: 'Сервер перезапускается. Переподключаемся…',
      submit: 'Продолжить',
      saving: 'Сохраняем…',
      saved: 'Сохранено.',
      retry: 'Повторить',
      back: 'Назад',
      close: 'Закрыть',
      previousImage: 'Предыдущее изображение',
      nextImage: 'Следующее изображение',
      imageCount: (current: number, total: number) => `${current} из ${total}`,
      editImage: 'Редактировать',
      imageEditGuidance: 'Что изменить в этой картинке',
      imageEditPlaceholder: 'Например: сделать шарф синим, а фон — светлее',
      imageEditHint: 'Будет создан новый вариант. Остальные детали постараемся сохранить.',
      applyImageEdit: 'Создать исправленный вариант',
      imageEditing: 'Создаём вариант…',
      cancel: 'Отмена',
      email: 'Почта',
      password: 'Пароль',
      name: 'Как вас зовут',
      emailPlaceholder: 'you@example.com',
      unexpectedError: 'Что-то пошло не так. Попробуйте ещё раз.',
      networkError: 'Не удалось связаться с сервером. Проверьте соединение.',
      /**
       * The reference code is shown next to a generation error: support uses it to
       * find the request log and the full logs (01-SPEC.md §7, E16-T3).
       */
      generationRef: (id: string) => `Код обращения: ${id}`,
      generationFailed:
        'В этот раз не получилось. Попробуйте ещё раз; если не выйдет — напишите нам.',
      /** Shown only when the brand promises a reply time (`BrandConfig.support.replyWithinHours`). */
      replyWithin: (hours: number) =>
        `Обычно отвечаем в течение ${hours} ${plural(hours, 'часа', 'часов', 'часов')}.`,
    },
    auth: {
      backToSignIn: 'Вернуться ко входу',
      signIn: 'Войти',
      signUp: 'Регистрация',
      signOut: 'Выйти',
      haveAccount: 'Уже есть аккаунт?',
      noAccount: 'Ещё нет аккаунта?',
      forgotLink: 'Забыли пароль?',
      login: {
        title: 'Вход',
        submit: 'Войти',
        failed: 'Неверная почта или пароль',
      },
      register: {
        title: 'Регистрация',
        closedTitle: 'Регистрация временно закрыта',
        closedSubtitle: 'Сейчас новые аккаунты создаёт администратор',
        closedText: 'Если вам нужен доступ, обратитесь к администратору сервиса.',
        submit: 'Создать аккаунт',
        passwordHint: 'Не короче 10 символов. Придумайте фразу, а не слово.',
        sentTitle: 'Проверьте почту',
        sentTo: (email: string) =>
          `Мы отправили ссылку для подтверждения на ${email}. Откройте её, чтобы закончить регистрацию.`,
        wrongAddress: 'Ошиблись адресом или письма нет несколько минут? Проверьте «Спам» или',
        startOver: 'зарегистрируйтесь заново',
        weakPassword: 'Такой пароль слишком легко угадать. Добавьте слов или замените на фразу.',
        offerLink: legalLink('registerTerms'),
        /*
         * Винительный падеж: «принимаю оферту и политику». Творительный
         * («политикой») остался здесь от прежней фразы «…ознакомились **с** офертой и
         * политикой» — предлог ушёл вместе с ней, падеж остался, и подпись перестала быть
         * грамотной. Проверка `termsRequired` ниже всё это время говорила правильно.
         */
        legalJoin: 'и',
        privacyLink: 'политику обработки персональных данных',
        /** Кликврап: непоставленная галочка — отказ, поэтому фраза вынесена в подпись чекбокса. */
        termsPrefix: 'Я принимаю',
        /*
         * Только возраст. Кто вправе загрузить фотографию конкретного ребёнка, спрашивается при
         * самой загрузке — там этот ребёнок уже есть, и ответ можно проверить.
         */
        adultLabel: 'Мне исполнилось 18 лет',
      },
      forgot: {
        title: 'Восстановление пароля',
        subtitle: 'Пришлём ссылку для смены пароля',
        submit: 'Прислать ссылку',
        sent: 'Если такой адрес у нас есть, письмо со ссылкой уже отправлено. Ссылка действует 30 минут.',
      },
      reset: {
        title: 'Новый пароль',
        subtitle: 'Придумайте пароль, которым ещё не пользовались',
        submit: 'Сохранить пароль',
        done: 'Пароль изменён. Теперь войдите с новым паролем.',
        badLink: 'Ссылка повреждена или устарела. Запросите новую.',
        requestNew: 'Запросить новую ссылку',
      },
      verify: {
        title: 'Подтверждение почты',
        titleDone: 'Почта подтверждена',
        pending: 'Проверяем ссылку…',
        done: 'Почта подтверждена. Всё готово к работе.',
        failed:
          'Ссылка недействительна или уже использована. Если вы уже подтвердили почту, просто войдите.',
        toSignIn: 'Войти',
        alreadyConfirmed: 'Почта уже подтверждена. Всё готово.',
        toApp: 'Перейти к книгам',
        bannerTitle: 'Подтвердите почту',
        bannerText: (email: string) =>
          `Мы отправили ссылку на ${email}. Пока почта не подтверждена, генерация недоступна.`,
        resend: 'Отправить письмо ещё раз',
        resent: 'Письмо отправлено повторно.',
      },
      validation: {
        emailRequired: 'Укажите почту',
        emailInvalid: 'Проверьте адрес: похоже, в нём опечатка',
        passwordRequired: 'Введите пароль',
        passwordShort: 'Пароль должен быть не короче 10 символов',
        nameRequired: 'Как к вам обращаться?',
        /* Называет тот же документ, на который ведёт ссылка, как бы он ни назывался. */
        termsRequired: `Примите ${legalLink('registerTerms')} и политику обработки персональных данных`,
        adultRequired: 'Подтвердите, что вам есть 18 лет',
      },
    },
    /**
     * Known API errors by stable codes (02-ARCHITECTURE.md §6.3).
     * The server text never reaches the UI: the active locale picks the caption.
     */
    errors: {
      quotaExceeded:
        'Квота исчерпана. Месячный лимит генераций обновится в начале следующего месяца, а лишнюю книгу можно отправить в архив.',
      rateLimited: 'Слишком много запросов подряд. Подождите минуту и повторите.',
      emailNotVerified: 'Подтвердите почту — до этого генерация недоступна.',
      queueUnavailable: 'Очередь генераций сейчас недоступна. Попробуйте ещё раз через минуту.',
      bookPublished:
        'Книга опубликована — правки запрещены. Снимите её с публикации на шаге «Экспорт».',
      bookNotPublishable: 'В книге пока нет разворотов. Соберите сюжет, прежде чем публиковать.',
      looksNotReady: 'Обновите план и утвердите образы и повторяющиеся детали перед рисованием.',
      charactersNotApproved: 'Сначала утвердите всех героев книги.',
      generationInProgress: 'Это уже создаётся. Дождитесь окончания и повторите.',
      storyDraftNotReady: 'Сначала подготовьте черновик сюжета.',
      notFound: 'Не найдено. Обновите страницу — возможно, это уже удалено.',
      payloadTooLarge: 'Файл слишком большой. Загрузите вариант поменьше.',
      unsupportedMediaType: 'Такой формат файла не поддерживается.',
      audioDurationInvalid:
        'Запись слишком короткая или длинная. Образец голоса — от 6 до 30 секунд, запись разворота — до 3 минут.',
      audioUnreadable:
        'Не удалось прочитать этот аудиофайл. Запишите заново здесь или сохраните запись в MP3, M4A или WAV и загрузите её.',
      photoConsentRequired: 'Перед обработкой фотографий подтвердите согласие.',
      publicationConsentRequired:
        'Перед публикацией подтвердите согласие на открытие книги по ссылке.',
      variantNotSelected: 'Сначала выберите вариант, который считаете похожим.',
      styleLocked:
        'Стиль уже зафиксирован первой готовой сценой. Создайте копию книги в другом стиле.',
    },
    usage: {
      title: 'Расход',
      generations: (used: number, limit: number) =>
        limit === 0
          ? `Генераций в этом месяце: ${used.toLocaleString('ru-RU')}`
          : `Генераций в этом месяце: ${used.toLocaleString('ru-RU')} из ${limit.toLocaleString('ru-RU')}`,
      books: (used: number, limit: number) =>
        limit === 0
          ? `Книг: ${used.toLocaleString('ru-RU')}`
          : `Книг: ${used.toLocaleString('ru-RU')} из ${limit.toLocaleString('ru-RU')}`,
      cost: (units: string) => `Потрачено: ${units} усл. ед.`,
      bookCost: (units: string) => `Стоимость этой книги: ${units} усл. ед.`,
      nearLimit: 'Квота почти исчерпана.',
      exhausted: 'Квота исчерпана — новые генерации не запускаются.',
    },
    settings: {
      title: 'Настройки',
      accountMenu: 'Меню аккаунта',
      profile: {
        title: 'Ваш аккаунт',
        changePassword: 'Сменить пароль',
        changePasswordHint: '— пришлём ссылку на почту.',
      },
      language: {
        title: 'Язык',
        description: 'Язык интерфейса. Язык текста выбирается отдельно для каждой новой книги.',
        label: 'Язык интерфейса',
        options: {
          ru: 'Русский',
          en: 'English',
        },
        saved: 'Язык сохранён.',
      },
      billing: {
        title: 'Биллинг',
        description: 'Тариф выбирается и оплачивается отдельно для каждой книги.',
      },
    },
    account: {
      delete: 'Удалить аккаунт',
      title: 'Удаление аккаунта',
      /*
       * То же, что написано в политике: человек читает этот экран вместо неё. Раньше здесь
       * обещалось немедленное и безвозвратное удаление, а политика обещала карантин, который
       * ещё можно отменить, — то есть единственный экран, который человек видит в этот момент,
       * говорил обратное его же правам. Срок подставляется: это `PURGE_QUARANTINE_DAYS`, и на
       * другом развёртывании он может быть иным.
       */
      warning: (quarantine: string) =>
        `Доступ закрывается сразу: ничего не видно и войти нельзя. Книги, герои и фотографии хранятся ещё ${quarantine}, и всё это время можно написать нам и остановить удаление. После этого файлы стираются из хранилища — отменить будет нельзя.`,
      password: 'Введите пароль, чтобы подтвердить',
      confirm: 'Удалить навсегда',
      cancel: 'Оставить аккаунт',
      wrongPassword: 'Пароль не подошёл.',
      done: (quarantine: string) =>
        `Аккаунт закрыт. Файлы будут стёрты через ${quarantine}; до этого можно написать нам и остановить удаление.`,
      writeToUs: 'Написать нам:',
    },
    books: {
      title: 'Мои книги',
      empty: 'Пока книг нет. Начните первую — стиль сказки выберете уже внутри.',
      add: 'Добавить книгу',
      create: 'Создать книгу',
      bookTitle: 'Название в библиотеке',
      bookTitleHint: 'Это рабочее название видно только вам. На сюжет и обложку оно не влияет.',
      bookLanguage: 'Язык книги',
      bookLanguageHint: 'На этом языке ИИ напишет название и весь текст сказки.',
      printSize: 'Размер книги',
      printSizeHint: 'Под этот размер рисуются иллюстрации и собирается PDF для печати.',
      languages: {
        ru: 'Русский',
        en: 'Английский',
        uz: 'Узбекский',
        ko: 'Корейский',
        ka: 'Грузинский',
        tr: 'Турецкий',
        tt: 'Татарский',
        fr: 'Французский',
        es: 'Испанский',
      },
      edited: (date: string) => `Изменена ${date}`,
      spreadsDrawn: (drawn: number, total: number) =>
        `Нарисовано разворотов: ${drawn.toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')}`,
      published: 'Опубликована',
      open: 'Открыть',
      duplicate: 'Копия',
      archive: 'В архив',
      status: {
        draft: 'Черновик',
        generating: 'Создаётся',
        ready: 'Готова',
        archived: 'В архиве',
      },
    },
    support: {
      characterTitle: (name: string) => `Помощь с героем «${name}»`,
      renditionTitle: (styleId: string) => `Варианты в стиле ${styleId}`,
    },
    library: {
      consent: {
        label:
          'Я являюсь законным представителем ребёнка или получил согласие изображённого лица и разрешаю обработку загруженных фотографий.',
        hint: 'Фотографии нужны только для того, чтобы нарисовать героя. Мы срезаем метаданные и храним файлы в закрытом хранилище. Согласие действует для всех фотографий в аккаунте.',
        required: 'Отметьте согласие на обработку фотографий — без него загрузка недоступна.',
        documentLink: 'Текст согласия',
        given: 'Вы дали согласие на обработку фотографий.',
        manage: 'Изменить в настройках',
        settingsTitle: 'Обработка фотографий',
      },
      photos: {
        title: 'Фотографии',
        deletedAfterApproval:
          'Рисунок утверждён, поэтому фотографии, по которым он сделан, удалены. Сам рисунок остаётся для всех книг.',
        uploadAgain: 'Загрузить новые фото и перерисовать',
        deletedNeedNewStyle:
          'Фотографии удалены после утверждения прошлого рисунка. В этой книге другой стиль, и нужен новый рисунок — загрузите фотографии снова, чтобы получилось похоже.',
        hintCharacter:
          'Три кадра: анфас, вполоборота и в полный рост. Чем ближе к списку ниже, тем больше сходства.',
        hintThing: 'Одна–три фотографии вещи с разных сторон.',
        hintLocation: 'Фотографии необязательны: место можно описать словами.',
        add: 'Выбрать файл',
        uploadPhoto: 'Загрузить',
        replace: 'Заменить',
        addPhotos: 'Добавить фото',
        remove: 'Убрать',
        uploading: 'Загружаем фотографию…',
        slots: {
          front: 'Анфас',
          profile: 'Вполоборота',
          full: 'В полный рост',
          extra: 'Дополнительное',
        },
        checklistTitle: 'Как снимать',
        checklist: {
          daylight: 'Дневной свет, лучше у окна',
          noFilters: 'Без фильтров и бьюти-режима',
          eyesOpen: 'Глаза открыты, лицо не перекрыто',
          plainBackground: 'Однотонный фон, ребёнок один в кадре',
          sharp: 'Кадр резкий, лицо занимает заметную часть',
        },
        issues: {
          slot_missing: 'Не хватает кадра для этого слота',
          low_resolution: 'Слишком маленькое разрешение: лицо получится размытым',
          duplicate_of_other_slot: 'Тот же файл уже стоит в другом слоте',
        },
        upload: {
          too_large: 'Файл больше 15 МБ. Уменьшите его или выберите другой.',
          unsupported_type: 'Такой формат не подойдёт. Нужен JPEG, PNG, WebP или HEIC.',
          too_small: 'Разрешение меньше 512 px по короткой стороне — сходство будет хуже.',
          failed: 'Не удалось загрузить фотографию. Попробуйте ещё раз.',
        },
      },
      prompt: {
        title: 'Промпт',
        compose: 'Собрать промпт',
        hint: 'Промпт уходит в модель на английском. Соберите его из анкеты и правьте руками, если нужно.',
        empty: 'Промпт ещё не собран.',
      },
      rendition: {
        noStyle: 'Сначала выберите стиль книги на шаге «Основное».',
        generate: 'Нарисовать',
        regenerate: 'Перегенерировать',
        approve: 'Утверждаю',
        approved: 'Утверждён',
        select: 'Выбрать',
        selected: 'Выбран',
        variantsTitle: 'Варианты',
        zoom: (index: number) =>
          `Рассмотреть вариант ${index.toLocaleString('ru-RU')} во весь экран`,
        empty: 'Пока ни одного варианта.',
        statuses: {
          empty: 'Ещё не рисовали',
          generating: 'Рисуем…',
          needs_review: 'Нужно посмотреть',
          approved: 'Утверждён',
          failed: 'Не получилось',
        },
        approveDeletesPhotos:
          'После утверждения рисунок остаётся для всех книг, а фотографии, по которым он сделан, удаляются.',
        drawWithoutPhotosConfirm:
          'Фотографий пока нет — рисунок не будет похож на человека и потратит попытку. Всё равно нарисовать?',
        redrawWithoutPhotosConfirm:
          'Фотографии удалены при утверждении рисунка, поэтому новый нарисуется без них и не будет похож на человека. Чтобы сохранить сходство, сначала загрузите новые фото. Всё равно нарисовать?',
        warnings: {
          photos_missing: 'Фотографий нет — сходства почти не будет.',
          photos_incomplete: 'Загружены не все три кадра — сходство будет хуже.',
          photo_low_resolution: 'Одна из фотографий слишком маленькая.',
        },
        attempts: (count: number) => `Попыток: ${count.toLocaleString('ru-RU')}`,
      },
      deadlock: {
        title: 'Похоже, дело в фотографиях',
        intro:
          'Три попытки уже позади. Дальше крутить генерацию бессмысленно: почти всегда виноваты исходные кадры, а не модель.',
        photosTitle: 'Что загружено',
        noPhotos: 'Фотографий нет ни одной — внешность героя определить невозможно.',
        fixPhotos: 'Заменить фотографии',
        callHuman: 'Позвать человека',
        commentLabel: 'Что не так — своими словами',
        sent: 'Заявка отправлена. Менеджер подключается и свяжется с вами.',
        failed: 'Не удалось отправить заявку. Попробуйте ещё раз.',
        generateAnyway: 'Всё-таки попробовать ещё раз',
      },
      usage: {
        unused: 'Пока не в книгах',
        inBooks: (count: number) =>
          `В ${count.toLocaleString('ru-RU')} ${plural(count, 'книге', 'книгах', 'книгах')}`,
        styles: (count: number) =>
          `Нарисован в ${count.toLocaleString('ru-RU')} ${plural(count, 'стиле', 'стилях', 'стилях')}`,
        spreads: (count: number) =>
          `${count.toLocaleString('ru-RU')} ${plural(count, 'разворот', 'разворота', 'разворотов')}`,
        blockedDelete: 'Пока объект в живой книге, его можно только отправить в архив.',
      },
      picker: {
        chooseKind: 'Выберите…',
        reuseTitle: 'Из вашей библиотеки',
        reuseHint:
          'Уже есть в вашей библиотеке. Добавленный сюда — тот же самый, с рисунком и описанием.',
        newTitle: 'Или создайте новый',
        usedIn: (books: number) => `В других книгах: ${books.toLocaleString('ru-RU')}`,
        notUsed: 'Пока ни в одной книге',
      },
      actions: {
        archive: 'В архив',
        delete: 'Удалить',
        save: 'Сохранить',
        addToBook: 'Добавить в книгу',
        removeFromBook: 'Убрать из книги',
        openLibrary: 'Открыть библиотеку',
        inBook: 'В книге',
      },
    },
    characters: {
      title: 'Герои',
      empty: 'Героев пока нет. Заведите первого — анкету можно дополнить позже.',
      emptyHint:
        'Герой живёт в библиотеке и переезжает из книги в книгу: заведёте один раз — пригодится во всех.',
      add: 'Добавить героя',
      create: 'Создать героя',
      addAnother: 'Добавить ещё героя',
      stepTitle: 'Герои',
      stepSubtitle: 'Добавьте героев книги и нарисуйте их лист в стиле этой книги.',
      lead: 'Главный герой',
      leadHint:
        'Никто не отмечен главным — им считается первый в списке. Отметьте явно, можно нескольких.',
      stepEmpty:
        'В книге пока нет героев. Добавьте хотя бы одного — без героев сюжет не соберётся.',
      stepEmptyHint: 'Возьмите готового героя из библиотеки или создайте нового прямо здесь.',
      stepEmptyHintFirst: 'Введите имя выше — фотографии добавите следующим шагом.',
      kinds: { child: 'Ребёнок', adult: 'Взрослый', pet: 'Питомец' },
      genders: { unspecified: 'Не выбрано', male: 'Мужской', female: 'Женский' },
      moreDetails: 'Подробнее (необязательно)',
      fields: {
        name: 'Имя',
        displayName: 'Как зовут дома',
        displayNameHint: 'Как зовут дома, например «Бусинка». Пригодится в репликах сказки.',
        roleHint: 'Кто он в этой истории, например «старшая сестра» или «самый смелый».',
        catchphraseHint: 'Любимая фраза. Сказка может вставить её раз-другой.',
        kind: 'Кто это',
        role: 'Роль в книге',
        ageYears: 'Возраст',
        gender: 'Пол',
        traits: 'Характер',
        traitsHint: 'Три–пять слов, по одному в строке',
        favouriteActivities: 'Любимые занятия',
        catchphrase: 'Словечко',
      },
    },
    things: {
      title: 'Любимые вещи',
      empty: 'Вещей пока нет. Их можно не добавлять — книга соберётся и без них.',
      emptyHint:
        'Любимый зайка или красные сапожки мелькают в сценах и обязательно попадают на обложку.',
      add: 'Добавить вещь',
      create: 'Создать вещь',
      stepTitle: 'Любимые вещи',
      stepSubtitle: 'Вещи мелькают в сценах и обязательно попадают на обложку.',
      optional: 'Шаг необязательный: книга без вещей собирается целиком.',
      stepEmpty: 'Вещей в книге нет — и это нормально. Можно идти дальше.',
      noApprovalNeeded:
        'Утверждать не нужно: в сцены идёт выбранный рисунок. Выберите другой, чтобы заменить.',
      drawn: 'Нарисовано',
      categories: {
        toy: 'Игрушка',
        clothing: 'Одежда',
        dish: 'Посуда',
        furniture: 'Мебель',
        other: 'Другое',
      },
      fields: { name: 'Название', category: 'Категория', description: 'Описание' },
    },
    locations: {
      title: 'Локации',
      empty: 'Локаций пока нет. Их можно не добавлять — тогда места придумает генератор сюжета.',
      emptyHint: 'Локация нужна, чтобы кухня в первой сцене и кухня в третьей были одной кухней.',
      add: 'Добавить локацию',
      create: 'Создать локацию',
      stepTitle: 'Локации',
      stepSubtitle: 'Общий план места без персонажей — референс для всех сцен в нём.',
      optional: 'Шаг необязательный: книга без локаций проходит весь путь до экспорта.',
      stepEmpty: 'Локаций в книге нет — и это нормально. Места придумает генератор сюжета.',
      noApprovalNeeded: 'Утверждать не нужно: референс уходит в сцены сразу после генерации.',
      spreadsTitle: 'Развороты',
      kinds: {
        room: 'Комната',
        kitchen: 'Кухня',
        outdoor: 'Улица',
        transport: 'Транспорт',
        imaginary: 'Выдуманное место',
        other: 'Другое',
      },
      fields: { name: 'Название', kind: 'Что за место', description: 'Описание' },
    },
    story: {
      title: 'Сюжет',
      subtitle: 'Опишите своими словами, о чём история, — ИИ разложит её по разворотам.',
      ai: {
        intro:
          'Повод, герои, любимые вещи, локации и лимит символов выбранной раскладки добавятся в бриф автоматически.',
        fields: {
          wishes: 'Пожелания к сюжету',
          wishesHint:
            'Несколько фраз своими словами: о чём история, чем заканчивается, чего лучше избегать.',
          wishesCounter: (current: number, max: number) =>
            `${current.toLocaleString('ru-RU')} / ${max.toLocaleString('ru-RU')}`,
          wishesTooLong: (max: number) =>
            `Пожелания к сюжету не могут быть длиннее ${max.toLocaleString('ru-RU')} символов.`,
        },
        generate: 'Сгенерировать сюжет',
        generateLocksPlan: 'Когда сюжет будет сгенерирован, тариф книги уже не поменять.',
        approvedLocked:
          'Сюжет утверждён. Можно править текст отдельных разворотов; создать или утвердить другой сюжет уже нельзя.',
        referenceLimit:
          'На одном из разворотов слишком много героев. Уменьшите их число в этой сцене.',
        regenerate: 'Перегенерировать',
        generating: 'Генерируем сюжет…',
        status: {
          queued: 'В очереди',
          running: 'Пишем историю…',
          ready: 'Готов к просмотру',
          failed: 'Не получилось',
        },
        preview: 'Предпросмотр',
        coverTitle: 'Название на обложке',
        coverTitleHint:
          'ИИ предложил название по сюжету. Проверьте или измените его перед утверждением.',
        editHint: 'Текст любого разворота можно поправить — правка останется в черновике.',
        save: 'Сохранить текст',
        saving: 'Сохраняем…',
        saved: 'Текст сохранён.',
        textFit: {
          left: (left: number, target: number) =>
            `Удобнее всего до ${ruCount(target)} ${plural(target, 'символа', 'символов', 'символов')} — осталось ${ruCount(left)}`,
          longer: (target: number) =>
            `Длиннее удобных ${ruCount(target)} ${plural(target, 'символа', 'символов', 'символов')}, но помещается`,
          smaller: (target: number) =>
            `Длиннее удобных ${ruCount(target)} ${plural(target, 'символа', 'символов', 'символов')} — текст наберём мельче`,
          overflow: (over: number) =>
            `Не помещается в раскладку даже самым мелким шрифтом: лишних знаков примерно ${ruCount(over)}. Сократите текст, иначе он закроет картинку`,
        },
        staleConfirm: (remaining: number | null | undefined | 'payment') =>
          `Картинка этого разворота нарисована к прежнему тексту. После сохранения она будет помечена как не соответствующая сюжету, ${ruRedrawCost(remaining)} Сохранить?`,
        promptUpdating: 'Обновляем описание иллюстрации и визуальные привязки…',
        promptFailed:
          'Не удалось обновить описание иллюстрации. Сохраните текст ещё раз, чтобы повторить.',
        applyLocks:
          'После утверждения состав героев фиксируется и перегенерировать сюжет нельзя. Текст отдельных разворотов можно править.',
        apply: 'Утвердить сюжет и посмотреть разворот',
        applyNeedsCharacters: (names: string) =>
          `Сюжет рисуется с утверждёнными героями. Утвердите рисунок: ${names}.`,
        applied: 'Применён',
        replaceConfirm:
          'В книге уже есть иллюстрации. Новый сюжет отвяжет их от разворотов, но файлы останутся в библиотеке. Продолжить?',
        underused: (name: string, count: number) =>
          `${name} появляется только в ${count.toLocaleString('ru-RU')} ${plural(count, 'развороте', 'разворотах', 'разворотах')} — лучше хотя бы в двух. Перегенерируйте сюжет или упомяните героя в тексте другого разворота.`,
        history: 'История черновиков',
        draftDate: (date: string) => `Черновик от ${date}`,
        failed: 'Сюжет не сгенерировался. Можно повторить запрос — пожелания сохранены.',
        current: 'Сюжет книги',
        currentHint:
          'Это утверждённый сюжет книги. Здесь можно править текст отдельных разворотов.',
        adminUnapply: 'Снять утверждение сюжета',
        adminUnapplying: 'Снимаем утверждение…',
        adminUnapplyHint:
          'Только для администратора: вернуть книгу к редактированию героев, вещей, локаций и черновика сюжета.',
        adminUnapplyConfirm:
          'Снять утверждение сюжета? Текущие развороты, варианты иллюстраций, видео, озвучка, обложки и готовые экспорты будут удалены из книги. Сам черновик сюжета сохранится и снова станет редактируемым.',
        adminUnapplied:
          'Утверждение снято. Герои, вещи, локации и черновик сюжета снова доступны для изменения.',
      },
      manual: {
        intro:
          'Каждый элемент — один разворот. Текстовые блоки можно переносить и перекрашивать независимо от иллюстрации.',
        empty: 'Разворотов пока нет. Добавьте первый или примените черновик ИИ.',
        add: 'Добавить разворот',
        spread: (index: number) => `Разворот ${index.toLocaleString('ru-RU')}`,
        text: 'Текст разворота',
        prompt: 'Описание картинки на английском',
        block: (index: number) => `Текстовый блок ${index.toLocaleString('ru-RU')}`,
        position: 'Положение',
        positions: {
          left: 'Слева',
          right: 'Справа',
          top: 'Сверху',
          bottom: 'Снизу',
          'top-left': 'Сверху слева',
          'top-right': 'Сверху справа',
          'bottom-left': 'Снизу слева',
          'bottom-right': 'Снизу справа',
        },
        textColor: 'Цвет текста',
        highlightColor: 'Цвет плашки',
        save: 'Сохранить разворот',
        saved: 'Разворот сохранён.',
        remove: 'Удалить',
        removeConfirm: 'Удалить этот разворот?',
        moveUp: 'Выше',
        moveDown: 'Ниже',
      },
    },
    looks: {
      advanced: 'Расширенные настройки',
      title: 'Постоянные детали книги',
      description: 'Одежда, предметы, люди и места с общим внешним видом на разных разворотах.',
      drawingReferences: 'Рисуем образы и повторяющиеся детали — иллюстрации начнутся сами.',
      referencesFailed:
        'Часть образов или деталей не нарисовалась. Повторите их в настройках — иллюстрации продолжатся сами.',
      redoCost: 'Другой вариант образа или детали расходует одну замену книги.',
      subjectsTitle: 'Повторяющиеся детали',
      subjectsHint:
        'Детали из истории, встречающиеся минимум на двух разворотах. Один утверждённый рисунок используется во всех их сценах.',
      subjectKinds: {
        person: 'Второстепенный персонаж',
        animal: 'Животное',
        object: 'Предмет',
        location: 'Место действия',
        other: 'Другая деталь',
      },
      noReference: 'Эталон ещё не нарисован',
      afterPayment: 'После оплаты',
      drawAfterPayment:
        'Эта деталь не нужна для первого бесплатного разворота. Нарисуем её после оплаты книги, на этапе генерации второго и следующих разворотов.',
      generateReference: 'Нарисовать эталон',
      drawingReference: 'Рисуем эталон…',
      referenceFailed: 'Не удалось нарисовать эталон. Можно повторить.',
      catalogTitle: 'Повторяющиеся элементы из библиотеки',
      fromLibrary: 'Используем утверждённый рисунок из библиотеки',
      libraryReferenceNeeded: 'Нарисуйте и утвердите этот элемент в библиотеке.',
      openLibraryItem: 'Открыть настройки элемента',
      planning: 'Находим образы и повторяющиеся детали по всей истории…',
      preparingTitle: 'Готовим иллюстрации',
      preparingHint: 'Это поможет сохранить единый вид героев и деталей на всех разворотах.',
      stale: 'Обновите план, чтобы он учитывал текущую историю, образы и повторяющиеся детали.',
      planFailed: 'Не удалось составить план. Попробуйте ещё раз.',
      guidance: 'Пожелания к внешнему виду',
      guidancePlaceholder:
        'Например: синяя шуба в лесу; игрушечный катер всегда красный; у продавца зелёная шляпа.',
      replanHint:
        'Неизменившиеся рисунки сохранятся. Изменённые образы и детали нарисуются заново, а их готовые сцены потребуют замены.',
      plan: 'Подобрать образы и детали',
      replan: 'Обновить план',
      noImage: 'Образ ещё не нарисован',
      base: 'Из утверждённого листа героя',
      approved: 'Утверждён',
      needsApproval: 'Требуется утверждение',
      generating: 'Рисуем образ…',
      generationFailed: 'Не удалось нарисовать образ. Можно повторить.',
      regenerate: 'Другой вариант',
      generate: 'Нарисовать',
      approve: 'Утвердить',
      chooseVariant: 'Выбрать и утвердить этот вариант',
      continue: 'Рисовать иллюстрации',
      spreads: (numbers: string) => `Развороты: ${numbers}`,
    },
    illustrations: {
      title: 'Иллюстрации',
      empty: 'Сначала добавьте развороты на шаге «Сюжет».',
      generateAll: 'Сгенерировать всё',
      generatingAll: 'Ставим в очередь…',
      generate: 'Сгенерировать',
      regenerate: 'Заменить картинку',
      editCurrent: 'Редактировать именно эту картинку',
      change: 'Изменить',
      editControls: (index: number) => `Редактировать разворот ${index.toLocaleString('ru-RU')}`,
      closeControls: (index: number) =>
        `Закрыть редактирование разворота ${index.toLocaleString('ru-RU')}`,
      replaceConfirm: (remaining: number) =>
        `Нарисовать разворот заново? Это спишет одну бесплатную замену (осталось ${remaining.toLocaleString('ru-RU')}). Текущая картинка останется среди вариантов.`,
      replaceConfirmUnlimited:
        'Нарисовать разворот заново? Текущая картинка останется среди вариантов.',
      replaceHint: 'Новый вариант будет создан заново по исходным данным разворота.',
      retry: 'Повторить',
      regenerationGuidance: {
        label: 'Что изменить в этой картинке',
        placeholder: 'Например: добавить красный шарф и сделать фон светлее',
        hint: 'Используются эта картинка, новый комментарий и необязательный референс; старый промпт и стиль не добавляются.',
      },
      regenerationReference: {
        label: 'Картинка-референс (необязательно)',
        hint: 'Добавьте образ костюма, предмета или другую визуальную подсказку для изменения.',
        uploading: 'Загружаем референс…',
        remove: 'Убрать',
        issues: {
          too_large: 'Файл больше 15 МБ. Уменьшите его или выберите другой.',
          unsupported_type: 'Нужен файл JPEG, PNG, WebP или HEIC.',
          too_small: 'Разрешение меньше 512 px по короткой стороне — детали могут потеряться.',
          failed: 'Не удалось загрузить референс. Попробуйте ещё раз.',
        },
      },
      progress: (ready: number, total: number) =>
        `Готово ${ready.toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')}`,
      eta: (seconds: number) =>
        `примерно ${Math.max(1, Math.ceil(seconds / 60)).toLocaleString('ru-RU')} мин`,
      statuses: {
        draft: 'Черновик',
        queued: 'В очереди',
        generating: 'Рисуем…',
        ready: 'Готово',
        failed: 'Ошибка',
      },
      stale:
        'Картинка больше не соответствует текущему сюжету или раскладке — нужна перегенерация.',
      promptUpdating: 'Обновляем описание иллюстрации и визуальные привязки…',
      promptFailed: 'Не удалось обновить описание иллюстрации. Повторите на шаге «Сюжет».',
      layout: 'Раскладка',
      layoutStale: (remaining: number | null | undefined | 'payment') =>
        `Картинка нарисована под текущую раскладку: после смены она перестанет подходить, ${ruRedrawCost(remaining)}`,
      layoutTextTooLong: (over: number) =>
        `Текст не поместится в эту раскладку даже самым мелким шрифтом (лишних знаков примерно ${ruCount(over)}) и закроет картинку. Сократите его на шаге «Сюжет».`,
      layoutChangeQuestion: 'Сменить раскладку?',
      settingsLocked: 'Раскладка, локация и положение текста открываются после оплаты.',
      pageSettings: 'Вёрстка разворота',
      location: 'Локация',
      noLocation: 'Без заданной локации',
      textPosition: (index: number, _total = 1) =>
        `Положение текста · блок ${index.toLocaleString('ru-RU')}`,
      variants: 'Варианты',
      zoom: (index: number) => `Увеличить иллюстрацию разворота ${index.toLocaleString('ru-RU')}`,
      noPreview: 'Иллюстрации пока нет',
      previewAlt: (index: number) => `Иллюстрация разворота ${index.toLocaleString('ru-RU')}`,
      variantAlt: (index: number) => `Вариант разворота ${index.toLocaleString('ru-RU')}`,
    },
    cover: {
      title: 'Обложки',
      subtitle:
        'Передняя и задняя обложки генерируются целиком и используются без дополнительной вёрстки.',
      titleLine: (title: string) => `Название на обложке: «${title}»`,
      frontTitle: 'Передняя обложка',
      backTitle: 'Задняя обложка',
      empty: 'Обложки пока нет',
      frontEmpty: 'Передней обложки пока нет',
      backEmpty: 'Задней обложки пока нет',
      generate: 'Создать обложку',
      regenerateConfirm: 'Нарисовать новый вариант обложки? Текущий останется среди вариантов.',
      regenerate: 'Новый вариант',
      generating: 'Ставим в очередь…',
      variants: 'Варианты обложки',
      previewAlt: 'Выбранная обложка книги',
      variantAlt: 'Вариант обложки',
      backPreviewAlt: 'Выбранная задняя обложка книги',
      backVariantAlt: 'Вариант задней обложки',
    },
    video: {
      title: 'Видео',
      subtitle: 'Добавьте мягкое движение и работу камеры к готовым иллюстрациям.',
      settings: 'Настройки видео',
      enabled: 'Создавать видео для этой книги',
      duration: 'Длительность сцены',
      seconds: (count: number) => `${count.toLocaleString('ru-RU')} сек`,
      batchWarningTitle: 'Создать видео для всех разворотов?',
      batchWarning: (count: number) =>
        `Будет создано видео для ${count.toLocaleString('ru-RU')} разворотов. Это может занять несколько минут.`,
      startBatch: 'Запустить пакет',
      startingBatch: 'Ставим пакет в очередь…',
      waitForSettings: 'Сначала дождитесь сохранения настроек.',
      progress: (ready: number, total: number) =>
        `Готово видео ${ready.toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')}`,
      eta: (seconds: number) =>
        `осталось примерно ${Math.max(1, Math.ceil(seconds / 60)).toLocaleString('ru-RU')} мин`,
      waitingForImage: (count: number) => `ждут иллюстрацию: ${count.toLocaleString('ru-RU')}`,
      disabled: 'Видео отключено. Готовые ролики сохранятся, новые задачи запускаться не будут.',
      empty: 'Сначала добавьте развороты на шаге «Сюжет».',
      prompt: 'Движение и камера — на английском',
      generate: 'Создать видео',
      regenerate: 'Перегенерировать',
      starting: 'Ставим в очередь…',
      imageRequired: 'Сначала создайте иллюстрацию этого разворота.',
      variants: 'Варианты видео',
      variant: (index: number) => `Вариант видео разворота ${index.toLocaleString('ru-RU')}`,
      preview: (index: number) => `Видео разворота ${index.toLocaleString('ru-RU')}`,
      imagePreview: (index: number) =>
        `Иллюстрация для видео разворота ${index.toLocaleString('ru-RU')}`,
      noPreview: 'Иллюстрации пока нет',
      statuses: {
        draft: 'Не создано',
        queued: 'В очереди',
        generating: 'Создаём…',
        ready: 'Готово',
        failed: 'Ошибка',
      },
    },
    admin: {
      title: 'Журнал ИИ-обращений',
      subtitle: 'Все вызовы моделей, их части, ответы, ошибки и повторы.',
      forbidden: 'Эта страница доступна только администраторам.',
      toApp: 'К книгам',
      select: 'Выберите обращение слева.',
      all: 'Все',
      filters: {
        provider: 'Провайдер',
        model: 'Модель',
        status: 'Статус',
        target: 'Цель',
        sort: 'Сортировка',
        user: 'ID пользователя',
        book: 'ID книги',
        from: 'Период с',
        to: 'Период по',
      },
      targets: {
        character_sheet: 'Лист героя',
        character_look: 'Образ героя',
        look_plan: 'План образов',
        book_subject: 'Эталон детали книги',
        thing_ref: 'Референс вещи',
        location_ref: 'Референс локации',
        scene_image: 'Иллюстрация сцены',
        cover: 'Обложка',
        back_cover: 'Задняя обложка',
        scene_video: 'Видео сцены',
        narration: 'Озвучка',
        story: 'Сюжет',
        scene_prompt: 'Промпт сцены',
        collage: 'Коллаж',
      },
      sorts: { time: 'По времени', cost: 'По стоимости', latency: 'По длительности' },
      statuses: {
        queued: 'В очереди',
        running: 'Выполняется',
        succeeded: 'Успешно',
        failed: 'Ошибка',
        cancelled: 'Отменено',
      },
      queue: {
        title: 'Очередь',
        queued: 'В очереди',
        running: 'Выполняется',
        failed: 'Упало',
        succeeded: 'Успешно',
        empty: 'Упавших задач нет.',
        retry: 'Повторить',
        retried: 'Задача поставлена в очередь заново.',
        attempt: (count: number) => `попытка ${count.toLocaleString('ru-RU')}`,
      },
      promptParts: 'Части промпта',
      roles: { style: 'Стиль', subject: 'Сцена', layout: 'Раскладка', suffix: 'Суффикс' },
      prompt: 'Итоговый промпт',
      negative: 'Негативный промпт',
      seed: 'Seed',
      size: 'Запрошенный размер (JSON)',
      parameters: 'Параметры запроса (JSON)',
      references: 'ID референсов',
      noReferences: 'Без референсов',
      latency: 'Время',
      cost: 'Стоимость',
      noBook: 'Без книги',
      invalidJson: 'Размер и параметры должны быть JSON-объектами.',
      replay: 'Повторить с правками',
      replayQueued: 'Повтор поставлен в очередь и действительно пойдёт к провайдеру.',
      copyCurl: 'Скопировать как curl',
      copied: 'Команда curl скопирована.',
      reveal: 'Показать референсы',
      referenceAlt: 'Раскрытый референс генерации',
      raw: 'Запрос, ответ и попытки',
      comparison: 'Сравнение прогонов',
      noResult: 'Результата пока нет',
      sceneId: 'ID разворота для применения',
      apply: 'Применить к развороту',
      applied: 'Результат явно применён к развороту.',
      promotions: {
        title: 'Промокоды',
        subtitle: 'Скидки от 0 до 100%, персональные выдачи и история применений.',
        create: 'Создать промокод',
        name: 'Название акции',
        code: 'Код',
        generate: 'Сгенерировать',
        discount: 'Скидка, %',
        targetEmail: 'Только для email',
        anyUser: 'Любой пользователь',
        maxTotal: 'Общий лимит',
        maxPerUser: 'Лимит на пользователя',
        unlimited: 'Без ограничений',
        startsAt: 'Начало действия',
        endsAt: 'Окончание',
        firstPurchase: 'Только первая оплаченная книга пользователя',
        activateImmediately: 'Активировать сразу',
        note: 'Внутренний комментарий',
        preview: 'Цены с этой скидкой',
        largeWarning:
          'Большая скидка заметно снижает доход от заказа. Система не будет её ограничивать.',
        freeWarning:
          '100% бесплатно откроют книгу и запустят все включённые генерации без перехода к оплате.',
        freeConfirm:
          'Этот промокод позволит бесплатно открыть книгу и запустить все генерации. Создать его?',
        save: 'Создать',
        saving: 'Сохраняем…',
        createError: 'Не удалось создать промокод. Проверьте код и даты.',
        active: 'Активен',
        paused: 'Приостановлен',
        scheduled: 'Запланирован',
        ended: 'Завершён',
        pause: 'Приостановить',
        resume: 'Возобновить',
        used: (count: number, limit: number | null) =>
          `использовано ${count.toLocaleString('ru-RU')}${limit === null ? '' : ` из ${limit.toLocaleString('ru-RU')}`}`,
        until: (date: string) => `до ${date}`,
        redeemed: 'Успешных применений',
        revenue: 'Полученная выручка',
        discountGiven: 'Предоставлено скидок',
        history: 'История применений',
        book: 'Книга',
        amount: 'Оплачено',
        status: 'Статус',
        useStatuses: { reserved: 'Ожидает оплаты', redeemed: 'Применён', released: 'Отменён' },
        empty: 'Промокодов пока нет.',
      },
      accounts: {
        title: 'Аккаунты',
        subtitle: 'Зарегистрированные пользователи, их книги и доступ к сервису.',
        registrationTitle: 'Самостоятельная регистрация',
        registrationHint: 'Когда выключена, страница регистрации показывает заглушку.',
        registrationEnabled: 'Регистрация открыта',
        registrationDisabled: 'Регистрация закрыта',
        registrationUpdated: 'Настройка регистрации сохранена.',
        economyModeTitle: 'Дешёвый режим',
        economyModeHint:
          'Для новых иллюстраций и видео MAX-тарифа использовать обычное качество: 1K и 720p вместо 2K и 1080p.',
        economyModeEnabled: 'Дешёвый режим включён',
        economyModeDisabled: 'Дешёвый режим выключен',
        economyModeUpdated: 'Настройка дешёвого режима сохранена.',
        createTitle: 'Создать аккаунт',
        createHint: 'Созданный здесь аккаунт доступен независимо от настройки регистрации.',
        createSubmit: 'Создать пользователя',
        createdMessage: 'Аккаунт создан.',
        accountExists: 'Аккаунт с такой почтой уже существует.',
        role: 'Роль',
        roles: { user: 'Пользователь', admin: 'Администратор' },
        markEmailVerified: 'Считать почту подтверждённой',
        toGenerations: 'Журнал ИИ',
        summary: 'Сводка по аккаунтам',
        totalAccounts: 'Всего аккаунтов',
        totalBooks: 'Всего книг',
        enabled: 'Включён',
        disabled: 'Отключён',
        search: 'Поиск',
        searchPlaceholder: 'Имя или email',
        filter: 'Статус аккаунта',
        sort: 'Сортировать по',
        direction: 'Направление',
        filters: { all: 'Все', enabled: 'Включённые', disabled: 'Отключённые' },
        sorts: {
          createdAt: 'Дате регистрации',
          name: 'Имени',
          email: 'Email',
          books: 'Количеству книг',
          status: 'Статусу аккаунта',
        },
        directions: { desc: 'По убыванию', asc: 'По возрастанию' },
        registered: 'Регистрация',
        emailVerified: 'почта подтверждена',
        emailNotVerified: 'почта не подтверждена',
        adminRole: 'Администратор',
        books: 'Книг',
        updated: 'Обновлена',
        showBooks: (count: number) => `Показать книги (${count.toLocaleString('ru-RU')})`,
        bookStatuses: {
          draft: 'Черновик',
          generating: 'Генерируется',
          ready: 'Готова',
          archived: 'В архиве',
        },
        enable: 'Включить аккаунт',
        disable: 'Отключить аккаунт',
        givePromo: 'Выдать промокод',
        disableConfirm: (email: string) =>
          `Отключить аккаунт ${email}? Все его активные сессии будут завершены.`,
        cannotDisableSelf: 'Нельзя отключить собственный аккаунт',
        enabledMessage: 'Аккаунт включён.',
        disabledMessage: 'Аккаунт отключён, активные сессии завершены.',
        empty: 'Аккаунты по выбранным условиям не найдены.',
        openBook: 'Открыть книгу',
        adminBookMode:
          'Административный режим: книга открыта от имени её владельца. Все изменения сохраняются непосредственно в его книге.',
        billingExempt: 'Для администратора оплата и квоты генераций отключены.',
        manualEdit: 'Открыть ручной редактор',
        replacementLimit: (used: number, total: number) =>
          `Повторные генерации: использовано ${used.toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')}`,
        replacementUnlimited: 'Повторные генерации: без ограничений',
        removeReplacementLimit: 'Снять лимит',
        restoreReplacementLimit: 'Вернуть лимит тарифа',
      },
      bookBuilder: {
        title: 'Ручной конструктор книг',
        editTitle: 'Ручной редактор книги',
        subtitle:
          'Соберите готовую книгу из своих текстов и медиа. Количество разворотов не ограничено тарифом.',
        editSubtitle:
          'Меняйте текст и напрямую заменяйте обложки, иллюстрации, видео и озвучку существующей книги.',
        owner: 'Владелец книги',
        ownerLocked: 'Владелец зафиксирован после первой загрузки файла.',
        style: 'Стиль и оформление',
        defaultLayout: 'Раскладка книги',
        defaultLayoutHint: 'Будет применена ко всем разворотам без своего переопределения.',
        spreadLayout: 'Раскладка разворота',
        inheritLayout: (layout: string) => `Как у книги — ${layout}`,
        layouts: {
          split: 'Текст и картинка',
          'full-bleed': 'Картинка на весь разворот',
          band: 'Горизонтальная полоса',
        },
        textPosition: 'Положение текста',
        primaryTextPosition: 'Первый текстовый блок',
        secondaryTextPosition: 'Второй текстовый блок',
        positions: {
          left: 'Слева',
          right: 'Справа',
          top: 'Сверху',
          bottom: 'Снизу',
          'top-left': 'Сверху слева',
          'top-right': 'Сверху справа',
          'bottom-left': 'Снизу слева',
          'bottom-right': 'Снизу справа',
        },
        bandImageRatio: 'Высота картинки',
        bookTitle: 'Название книги',
        bookSubtitle: 'Подзаголовок',
        dedication: 'Посвящение',
        covers: 'Обложки',
        frontCover: 'Передняя обложка',
        backCover: 'Задняя обложка',
        spreads: 'Развороты',
        spreadsHint: 'Текст и иллюстрация обязательны. Видео и озвучка добавляются при наличии.',
        addSpread: 'Добавить разворот',
        spread: (index: number) => `Разворот ${index.toLocaleString('ru-RU')}`,
        spreadText: 'Текст разворота',
        primaryBlockText: 'Текст первого блока',
        secondaryBlockText: 'Текст второго блока',
        illustration: 'Иллюстрация',
        videoOptional: 'Видео · необязательно',
        narrationOptional: 'Озвучка · необязательно',
        up: 'Выше',
        down: 'Ниже',
        remove: 'Удалить',
        noFile: 'Файл не выбран',
        chooseFile: 'Выбрать файл',
        replaceFile: 'Заменить файл',
        clearFile: 'Убрать',
        uploading: 'Загружаем…',
        readySummary: (spreads: number, files: number) =>
          `${spreads.toLocaleString('ru-RU')} разворотов · ${files.toLocaleString('ru-RU')} файлов загружено`,
        publishHint: 'Книга сразу появится у владельца и получит публичную ссылку.',
        publish: 'Собрать и опубликовать',
        publishing: 'Собираем книгу…',
        save: 'Сохранить изменения',
        saving: 'Сохраняем…',
        saved: 'Изменения сохранены',
        published: 'Книга опубликована',
        openPreview: 'Открыть просмотр',
        copyLink: 'Скопировать ссылку',
        validation: {
          main: 'Укажите владельца, стиль и название книги.',
          covers: 'Загрузите переднюю и заднюю обложки.',
          spread: (index: number) =>
            `Добавьте текст и иллюстрацию для разворота ${index.toLocaleString('ru-RU')}.`,
          secondBlock: (index: number) =>
            `Добавьте текст второго блока для разворота ${index.toLocaleString('ru-RU')}.`,
        },
      },
      usage: {
        title: 'Дашборд генераций',
        subtitle: 'Live-статистика запусков, ошибок и расходов. Повтор — новый запуск той же цели.',
        ranges: { day: '24 часа', week: '7 дней' },
        live: 'Текущее состояние генераций',
        liveUpdated: (date: string) => `Обновлено ${date}`,
        runningNow: 'Выполняются сейчас',
        queuedNow: 'Ждут в очереди',
        refreshHint: 'Данные обновляются каждые 5 секунд.',
        summary: 'Сводка за период',
        total: 'Всего генераций',
        regenerations: 'Повторные генерации',
        users: 'Активные пользователи',
        booksDetail: (count: number) => `${count.toLocaleString('ru-RU')} книг`,
        successRate: 'Успешность',
        failedDetail: (count: number) => `${count.toLocaleString('ru-RU')} ошибок`,
        cost: 'Стоимость, units',
        averageLatency: 'Среднее время',
        adminReplaysDetail: (count: number) =>
          `${count.toLocaleString('ru-RU')} повторов из админки`,
        timeline: 'Динамика генераций',
        legend: { total: 'Все', failed: 'Ошибки', regenerations: 'Повторные' },
        chartLabel: 'График генераций и повторных генераций по времени',
        bucketTooltip: (label: string, total: number, regenerations: number, failed: number) =>
          `${label}: всего ${total}, повторных ${regenerations}, ошибок ${failed}`,
        regenerationPoint: (count: number) => `Повторных генераций: ${count}`,
        byTarget: 'По типу генерации',
        targetDetail: (regenerations: number, failed: number, cost: string) =>
          `повторных ${regenerations} · ошибок ${failed} · стоимость ${cost}`,
        topUsers: 'Самые активные пользователи',
        user: 'Пользователь',
        generationsShort: 'Генерации',
        regenerationsShort: 'Повторы',
        errorsShort: 'Ошибки',
        empty: 'За выбранный период генераций нет.',
      },
    },
    preview: {
      readerLabel: (title: string) => `Просмотр книги «${title}»`,
      unlockRest: 'Это бесплатный фрагмент · Открыть всю книгу',
      backToBuilder: 'Вернуться в конструктор',
      loadError: 'Не удалось загрузить книгу для просмотра.',
      cover: 'Обложка',
      backCover: 'Задняя обложка',
      spread: (index: number) => `Разворот ${index.toLocaleString('ru-RU')}`,
      counter: (current: number, total: number) =>
        `${current.toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')}`,
      fullscreen: 'На весь экран',
      exitFullscreen: 'Выйти из полного экрана',
      previous: 'Предыдущая страница',
      next: 'Следующая страница',
      navigationLabel: 'Навигация по книге',
      pagePicker: 'Выбор страницы',
      help: 'Потяните страницу за край, листайте стрелками клавиатуры или свайпом.',
      coverAlt: (title: string) => `Иллюстрация обложки книги «${title}»`,
      backCoverAlt: (title: string) => `Иллюстрация задней обложки книги «${title}»`,
      sceneAlt: (index: number, title: string) =>
        `Иллюстрация разворота ${index.toLocaleString('ru-RU')} книги «${title}»`,
      playNarration: 'Воспроизвести озвучку',
      pauseNarration: 'Пауза',
      resumeNarration: 'Продолжить',
      repeatNarration: 'Повторить разворот',
      muteNarration: 'Выключить звук',
      unmuteNarration: 'Включить звук',
      narrationUnavailable: 'Для этого разворота озвучки нет',
    },
    narration: {
      title: 'Озвучка',
      createVoice: 'Создать новый голос',
      newVoiceName: 'Название нового голоса',
      voiceName: 'Например, мамин голос',
      addVoice: 'Добавить голос',
      creatingVoice: 'Создаём голос…',
      voiceConsent: 'Я подтверждаю согласие владельца голоса на обработку и клонирование записи.',
      voiceConsentLink: 'Текст согласия',
      sampleHint:
        'Образец рассчитан примерно на 20 секунд: один голос, тихая комната, без музыки и фильтров.',
      referenceScriptLabel: 'Текст для чтения',
      referenceScript:
        'Я читаю этот текст спокойным, медленным и естественным голосом — именно так я хочу, чтобы звучала озвучка этой книги. Пусть каждое слово произносится ясно, мягко и с доброй интонацией, а история разворачивается неторопливо, словно мы вместе листаем страницы перед сном.',
      referenceScriptHint: 'Прочитайте текст естественно, без спешки и длинных пауз.',
      sampleTranscript: 'Что человек говорит на записи (необязательно)',
      sampleTranscriptPlaceholder: 'Введите точный текст записи, если вы его знаете',
      transcriptExact: 'Этот текст дословно совпадает с записью',
      transcriptHint:
        'Точную расшифровку мы передадим вместе с аудио — так голос получится ближе к оригиналу. Если не уверены, оставьте флажок выключенным.',
      chooseSample: 'Выбрать аудиофайл',
      replaceChosenSample: 'Выбрать другой файл',
      sampleReady: (fileName: string) => `Образец готов: ${fileName}`,
      sampleReadyLength: (seconds: number) => `Образец готов: ${seconds} с`,
      sampleTooShort: (seconds: number) =>
        `Запись длится ${seconds} с. Образцу нужно хотя бы 6 секунд — прочитайте текст целиком.`,
      sampleTooLong: (seconds: number) =>
        `Запись длится ${seconds} с. Образец — не длиннее 30 секунд.`,
      voiceNeedsSample: 'Образца пока нет — добавьте его, чтобы использовать голос.',
      editVoice: 'Изменить',
      renameVoice: 'Название голоса',
      saveVoiceName: 'Сохранить название',
      recordSample: 'Записать образец',
      rerecordSample: 'Перезаписать',
      uploadSample: 'Или выбрать аудиофайл',
      replaceSample: 'Заменить аудиофайлом',
      deleteVoice: 'Удалить голос',
      deleteVoiceConfirm: (voiceName: string) =>
        `Удалить голос «${voiceName}»? Уже созданная озвучка останется в книге.`,
      voiceForSynthesis: 'Голос для озвучки',
      femaleVoice: 'Женский голос',
      maleVoice: 'Мужской голос',
      previewVoice: (voiceName: string) => `Прослушать «${voiceName}»`,
      pauseVoicePreview: (voiceName: string) => `Пауза: «${voiceName}»`,
      previewAfterSynthesis: 'Образец появится после первой озвучки этим голосом.',
      exactTranscriptSaved: 'К образцу сохранена точная расшифровка.',
      useOwnVoice: 'Добавить свой голос',
      uploadInstead: 'или загрузить файл',
      recordInstead: 'или записать здесь',
      fileContents: 'Что в файле?',
      fileIsScript: 'Я читаю текст выше',
      fileHowTo: 'Можно записать голосовое в Telegram и загрузить тот же файл.',
      fileIsOther: 'Другая запись',
      editVoiceNamed: (voiceName: string) => `Изменить «${voiceName}»`,
      closeVoiceNamed: (voiceName: string) => `Закрыть «${voiceName}»`,
      listen: 'Прослушать',
      pausePreview: 'Пауза',
      bySpread: 'Или озвучьте по разворотам',
      generateAll: 'Озвучить всю книгу',
      replaceRecordedConfirm: (count: number) =>
        count === 1
          ? 'В одном развороте ваша собственная запись. Озвучка всей книги заменит её выбранным голосом. Продолжить?'
          : `В ${count.toLocaleString('ru-RU')} разворотах ваши собственные записи. Озвучка всей книги заменит их выбранным голосом. Продолжить?`,
      spread: (index: number) => `Разворот ${index.toLocaleString('ru-RU')}`,
      empty: 'Сначала добавьте развороты на шаге «Сюжет».',
      stale:
        'Текст изменился после записи. Старая озвучка продолжает играть, но её стоит обновить.',
      record: 'Записать',
      rerecord: 'Перезаписать',
      uploadAudio: 'Загрузить аудио',
      replaceAudio: 'Заменить аудио',
      stopRecording: 'Остановить запись',
      microphoneDenied:
        'Не удалось получить доступ к микрофону. Разрешите его в настройках браузера и попробуйте снова.',
      synthesize: 'Синтезировать',
      resynthesize: 'Пересинтезировать',
      remove: 'Удалить озвучку',
      actionFailed: 'Не удалось выполнить действие с озвучкой. Попробуйте ещё раз.',
      statuses: {
        draft: 'Не озвучен',
        queued: 'В очереди',
        generating: 'Озвучиваем…',
        ready: 'Готово',
        failed: 'Ошибка',
      },
    },
    export: {
      title: 'Экспорт',
      formatTitle: 'Формат файла',
      formats: {
        pdf_print: {
          title: 'PDF для печати',
          description: 'Постраничный A5: обложка, половины разворотов и задняя обложка.',
        },
        pdf_screen: {
          title: 'PDF для экрана',
          description: 'Один разворот на страницу, без вылетов и пустых страниц.',
        },
        png_zip: {
          title: 'Архив PNG',
          description: 'Каждая физическая страница отдельным PNG в ZIP-архиве.',
        },
      },
      create: 'Создать файл',
      submitting: 'Ставим в очередь…',
      cached: 'Книга не менялась — готовый файл взят из кэша.',
      failed: 'Не удалось получить экспорт. Попробуйте ещё раз.',
      retry: 'Попробовать снова',
      history: 'История экспортов',
      empty: 'Экспортов пока нет.',
      emptyHint:
        'Готовый файл появится здесь после первой сборки. Ссылка на скачивание живёт 15 минут.',
      download: 'Скачать',
      progress: (value: number) => `Готово на ${value.toLocaleString('ru-RU')}%`,
      linkExpires: (date: string) => `Ссылка действует до ${date}.`,
      status: {
        queued: 'В очереди',
        running: 'Собирается',
        succeeded: 'Готов',
        failed: 'Ошибка',
        cancelled: 'Отменён',
      },
    },
    publish: {
      title: 'Публикация',
      subtitle:
        'Опубликуйте книгу — получится ссылка только для чтения. Её можно отправить бабушке или крёстной: вход не потребуется.',
      locksEditing: 'Пока книга опубликована, её нельзя редактировать.',
      unpublishedHint: 'Пока книга не опубликована, ссылки на неё не существует.',
      publish: 'Опубликовать',
      publishing: 'Публикуем…',
      unpublish: 'Снять с публикации',
      unpublishing: 'Снимаем…',
      published: (date: string) => `Опубликовано ${date}.`,
      linkLabel: 'Публичная ссылка',
      readyTitle: 'Книга готова к встрече с читателями',
      readyText: 'Отправьте её близким или покажите QR-код — получателю не понадобится аккаунт.',
      share: 'Поделиться',
      shareText: (title: string) => `Открывайте книгу «${title}»`,
      shared: 'Книга отправлена',
      copy: 'Скопировать ссылку',
      copied: 'Ссылка скопирована',
      copyFailed: 'Не удалось скопировать ссылку',
      showQr: 'Показать QR-код',
      qrEyebrow: 'Сказка всегда рядом',
      qrTitle: 'QR-код книги',
      qrDescription: 'Покажите код на экране другого устройства или скачайте красивую карточку.',
      qrInstruction: 'Наведите камеру, чтобы открыть книгу',
      qrAlt: (title: string) => `QR-код книги «${title}»`,
      downloadQr: 'Скачать карточку',
      qrStableHint: 'QR ведёт на постоянную ссылку книги и не изменится после перепубликации.',
      open: 'Открыть как читатель',
      lockNotice:
        'Пока книга опубликована, правки запрещены: читатель по ссылке не должен увидеть сказку в разобранном виде. Снимите с публикации, чтобы что-то изменить.',
      /** The link stays the same, and that is worth saying before it gets shared. */
      stableLinkHint:
        'Ссылка у книги одна и не меняется: после повторной публикации она снова заработает.',
      notPublishable: 'В книге пока нет разворотов. Соберите сюжет, прежде чем публиковать.',
      /**
       * Отдельное согласие на распространение (ст. 10.1 152-ФЗ): по ссылке
       * данные ребёнка открываются неопределённому кругу лиц, и подтверждать
       * это нужно осознанно, а не нажатием одной кнопки.
       */
      consent: {
        label:
          'Я подтверждаю согласие на открытие книги по публичной ссылке и полномочия в отношении всех изображённых и озвученных лиц.',
        documentLink: 'Текст согласия',
        hint: 'Ссылку сможет открыть любой, кто её получит, и переслать дальше. Публикацию можно отменить в любой момент.',
      },
      failed: 'Не удалось изменить публикацию. Попробуйте ещё раз.',
    },
    publicBook: {
      loading: 'Открываем книгу…',
      notFoundTitle: 'Книга недоступна',
      notFoundText:
        'Ссылка неверна или книгу сняли с публикации. Попросите новую ссылку у того, кто её присылал.',
      home: `Что такое ${name}`,
      backToLanding: 'На главную',
      reportLabel: 'Пожаловаться на книгу:',
      reportLink: 'Как мы рассматриваем жалобы',
      makeYourOwn: 'Сделать свою книгу',
    },
    report: {
      title: 'Пожаловаться на книгу',
      intro: `Если в книге, опубликованной через ${name}, есть то, чему не место в сети, — чужая фотография без согласия, ребёнок в сексуальном контексте, что угодно, что вы считаете незаконным, — напишите нам. Аккаунт не нужен, имя называть не обязательно.`,
      howTitle: 'Куда написать',
      howText:
        'Пришлите ссылку на книгу, если она у вас есть, и всё, что знаете о том, что увидели. Ссылки довольно, чтобы начать; если её нет, жалоба всё равно имеет смысл.',
      nextTitle: 'Что будет дальше',
      next: [
        'Доступ к книге закрывается, как только мы поняли, в чём дело.',
        'Данные за книгой сохраняются, даже если вы просите их удалить: узнав о жалобе, мы обязаны сохранить их как доказательство, и удаление на этом шаге было бы отдельным нарушением.',
        'Мы отвечаем вам, что сделали. Где закон требует сообщить о находке самим, мы сообщаем.',
      ],
      responseTitle: 'Что с ней происходит',
      responseText: 'Каждую жалобу читает человек и реагирует на неё.',
      privacyTitle: 'О вас',
      privacyText: 'Жалоба — это письмо нам, а не публикация: кто её отправил, мы не раскрываем.',
    },
    monetization: {
      chooseTitle: 'Выберите формат книги',
      chooseSubtitle:
        'Тариф определяет объём сюжета и возможности готовой книги. Оплата будет после первого разворота.',
      popular: 'Популярный выбор',
      pagesShort: (count: number) => `${count} ${plural(count, 'страница', 'страницы', 'страниц')}`,
      selected: 'Выбрано',
      select: 'Выбрать',
      lockedTitle: 'Тариф',
      lockedError: 'Сюжет уже написан под этот тариф, поэтому его не поменять.',
      locked: (pages: number) =>
        `Сюжет написан на ${pages} ${plural(pages, 'страницу', 'страницы', 'страниц')}, поэтому тариф уже не поменять.`,
      summaryPaid: (price: string) => `${price}, оплачено`,
      summaryUnpaid: (price: string) => `${price}, оплата после бесплатного первого разворота`,
      included: 'Что входит',
      planLimit: (used: number, limit: number) =>
        `${used.toLocaleString('ru-RU')} из ${limit.toLocaleString('ru-RU')} по тарифу`,
      /**
       * Plan comparison bullets. The set of strings is identical across all cards: otherwise
       * it reads as if the PDF and the family link exist only in the cheapest plan,
       * although they are included in all of them.
       */
      features: {
        pages: (count: number, spreads: number) =>
          `${count} ${plural(count, 'страница', 'страницы', 'страниц')}: ${spreads} ${plural(spreads, 'разворот', 'разворота', 'разворотов')} и обложка`,
        characters: (count: number) => `До ${count} ${plural(count, 'героя', 'героев', 'героев')}`,
        props: (things: number, locations: number) =>
          `До ${things} ${plural(things, 'вещи', 'вещей', 'вещей')} и ${locations} ${plural(locations, 'локации', 'локаций', 'локаций')}`,
        resolution: (value: string) =>
          value === '2K' ? 'Иллюстрации повышенного качества' : 'Иллюстрации стандартного качества',
        video: (resolution: string) =>
          resolution === '1080p' ? 'Видео повышенного качества' : 'Видео стандартного качества',
        noVideo: 'Без видео',
        narration: 'Озвучка голосом близкого',
        noNarration: 'Без озвучки',
        share: 'PDF для экрана и семейная ссылка',
        printStandard: 'Макет для печати в обычном качестве',
        printHigh:
          brand.printHighQuality['ru'] ?? brand.printHighQuality[brand.defaultLocale] ?? '',
        noPrint: 'Без макета для печати',
        replacements: (count: number, withVideo: boolean) =>
          `${count} ${plural(count, 'бесплатная замена', 'бесплатные замены', 'бесплатных замен')} иллюстраций${withVideo ? ' и видео' : ''}`,
      },
      plans: {
        book: {
          name: 'Книга',
          summary: 'Классическая персональная книга для чтения с экрана и печати.',
        },
        live_book: {
          name: 'Живая книга',
          summary: 'Персональная книга с ожившими иллюстрациями.',
        },
        gift_book: {
          name: 'Книга-подарок',
          summary: 'Максимальная версия с озвучкой и макетом высокого качества для печати.',
        },
      },
      preview: {
        title: 'Создаём первый разворот бесплатно',
        text: 'Когда он будет готов, вы увидите результат и сможете продолжить создание книги.',
        readyTitle: 'Бесплатный разворот готов',
        readyText: (remaining: number, plan: string) =>
          `Нравится? Тариф «${plan}» дорисует остальные развороты (${remaining.toLocaleString('ru-RU')}), обе обложки и всё, что в него входит.`,
        lockedSpread: (index: number) =>
          `Разворот ${index.toLocaleString('ru-RU')} · будет нарисован после оплаты`,
      },
      payment: {
        title: 'Первый разворот готов',
        text: (remaining: number, plan: string) =>
          `Сюжет сохранён. После оплаты дорисуем остальные развороты (${remaining.toLocaleString('ru-RU')}), обе обложки и всё, что входит в тариф «${plan}».`,
        required: 'Это откроется после оплаты.',
        textFree: (remaining: number, plan: string) =>
          `Сюжет сохранён. Как только вы получите книгу, дорисуем остальные развороты (${remaining.toLocaleString('ru-RU')}), обе обложки и всё, что входит в тариф «${plan}».`,
        previewAlt: 'Ваш бесплатный разворот',
        oneTime: 'Оплата один раз · Без подписки',
        alternativePrice: (price: string, currency: string) =>
          `Платите из страны, где в ходу ${currency}? Списано будет ${price}.`,
        action: (price: string) => `Оплатить ${price}`,
        processing: 'Переходим к оплате…',
        error: 'Не удалось продолжить. Попробуйте ещё раз.',
        promoToggle: 'Есть промокод?',
        promoLabel: 'Промокод',
        promoPlaceholder: 'Введите код',
        promoApply: 'Применить',
        promoApplied: (percent: number) => `Промокод применён · скидка ${percent}%`,
        promoPlanHint: 'Цена пересчитана. Проверьте выбранный тариф перед оплатой.',
        promoUnlocked: 'Книга доступна без оплаты.',
        promoInvalid: 'Промокод недействителен или больше недоступен.',
        /** The server got no checkbox: only a page opened before it existed sends that. */
        freeAction: 'Получить книгу бесплатно',
        freeProcessing: 'Открываем книгу…',
        returnPending: brand.paymentProviderName
          ? `Проверяем оплату в ${brand.paymentProviderName}…`
          : 'Проверяем оплату…',
        returnSucceeded: 'Книга открыта. Всё, что входит в её тариф, уже доступно.',
        returnCanceled: 'Платёж отменён. Вы можете попробовать оплатить ещё раз.',
        returnError: 'Не удалось проверить платёж. Обновите страницу или попробуйте позже.',
        lockedStep: {
          cover: 'Обе обложки рисуются после оплаты — с теми же героями и в том же стиле.',
          video: 'Каждый разворот оживает после оплаты.',
          narration: 'Озвучка открывается после оплаты: выберите голос или запишите свой.',
          export: 'После оплаты можно поделиться ссылкой для семьи и скачать PDF.',
        },
        goToPay: 'Посмотреть бесплатный разворот и открыть книгу',
        goToPreview: 'Посмотреть первый разворот',
        /** Clickwrap: an unticked box is a refusal, so the sentence is a checkbox label. */
        legalPrefix: 'Я принимаю',
        offerLink: legalLink('paywallTerms'),
        legalJoin: 'и правила',
        refundLink: legalLink('paywallRefund'),
      },
      replacementsLeft: (remaining: number, total: number) =>
        `Осталось бесплатных замен: ${remaining} из ${total}`,
      replacementPack: {
        title: 'Включённые изменения закончились',
        text: (count: number) =>
          `Добавьте ещё ${count} ${plural(count, 'изменение', 'изменения', 'изменений')} для иллюстраций и видео этой книги. После оплаты вернитесь к нужному изменению.`,
        action: (price: string) => `Добавить за ${price}`,
        processing: 'Переходим к оплате…',
        error: 'Не удалось добавить изменения. Попробуйте ещё раз.',
      },
      unavailable: 'Эта возможность не входит в выбранный тариф.',
      unavailableStep: {
        video: 'В тарифах с видео каждый разворот оживает на несколько секунд.',
        narration: 'В тарифах с озвучкой книгу читают вслух выбранным голосом.',
      },
    },
    builder: {
      back: 'Все книги',
      preview: 'Просмотр',
      navigationLabel: 'Шаги конструктора',
      mainTitle: 'Основное',
      save: {
        saved: 'Сохранено',
        waiting: 'Есть изменения…',
        saving: 'Сохраняем…',
        error: 'Не удалось сохранить',
      },
      fields: {
        title: 'Название в библиотеке',
        subtitle: 'Подзаголовок',
        occasion: 'Повод',
        age: 'Возраст ребёнка',
        ageHint: 'Для кого книга. Под этот возраст подбираются слова и длина текста.',
      },
      style: 'Стиль картинок',
      styleSuggestionsLabel: 'Подсказки стиля',
      styleSuggestions: {
        hyperrealistic: 'Гиперреалистично',
        anime: 'Аниме',
        softCartoon: 'Мягкий мультфильм (как Pixar)',
        watercolor: 'Акварель',
        flat: 'Плоская детская иллюстрация',
        classic: 'Классическая книжная графика',
      },
      ownStyle: 'Описать свой стиль',
      ownStyleLabel: 'Как должны выглядеть картинки?',
      ownStyleHint:
        'Своими словами: техника, цвета, настроение. Первые иллюстрации покажут, как выглядит ваш стиль.',
      ownStylePlaceholder: 'Мягкая акварель, приглушённые цвета, как в старой книжке сказок',
      ownStyleSave: 'Выбрать этот стиль',
      ownStyleYours: 'Ваш стиль',
      styleLocked: 'Стиль зафиксирован первой готовой иллюстрацией.',
      layout: 'Раскладка книги',
      layoutHints: {
        split: 'Текст на одной странице, картинка на другой',
        'full-bleed': 'Одна картинка на обе страницы, текст поверх',
        band: 'Широкая картинка, текст на полосе под ней',
      },
      layoutNames: {
        split: 'Текст + иллюстрация',
        'full-bleed': 'На весь разворот',
        band: 'Панорама с полосой',
      },
      alternateSides: 'Чередовать текстовую страницу слева и справа',
      layoutConfirm: (count: number) =>
        `Смена раскладки сделает неактуальными иллюстрации: ${count}. Продолжить?`,
      steps: {
        main: 'Основное',
        characters: 'Герои',
        things: 'Вещи',
        locations: 'Локации',
        story: 'Сюжет',
        illustrations: 'Иллюстрации',
        cover: 'Обложка',
        video: 'Видео',
        narration: 'Озвучка',
        export: 'Экспорт',
      },
      stepStates: {
        empty: 'Пусто',
        working: 'В работе',
        ready: 'Готово',
        error: 'Нужно внимание',
        optional: 'Необязательно',
        unavailable: 'Нет в тарифе',
      },
      locked: {
        illustrations:
          'Иллюстрации станут доступны, когда все герои книги будут утверждены в её стиле.',
        illustrationsCount: (pending: number, total: number) =>
          `Утверждено героев: ${(total - pending).toLocaleString('ru-RU')} из ${total.toLocaleString('ru-RU')}.`,
        noCharacters: 'Сначала добавьте хотя бы одного героя.',
      },
      soon: 'Этот шаг появится в следующих эпиках.',
      preStoryReadOnly:
        'Сюжет принят. Этот шаг теперь доступен только для просмотра; редактирование продолжается со вкладки «Сюжет».',
      goToStep: (label: string) => `Перейти к шагу «${label}»`,
      nextStep: (label: string) => `Далее: ${label}`,
    },
  } as const;
}

export type Dictionary = Localized<ReturnType<typeof ruSourceOf>>;

/** The dictionary is built once per brand; the UI receives strings via useI18n. */
export function createRu(brand: BrandConfig, legal: LegalConfig): Dictionary {
  return ruSourceOf(brand, legal);
}
